import { getTheme, IPivotStyles, Pivot, PivotItem, Spinner, SpinnerSize, Stack, StackItem } from '@fluentui/react';
import { RouteComponentProps, useHistory, useLocation, useParams, withRouter } from 'react-router-dom';
import { QueueTitle } from './QueueTitle';
import React, { useState, useEffect } from 'react';
import { getQueueConfig } from '../../services/gmc';
import { ContentType, QueueConfiguration, trackException } from 'services';
import { AdminContext } from 'Store/Admin';
import { checkThePercentageAndCasesAreValid, checkTheTimeIsValid } from 'components/util/validation';
import { CaseUrgencyTab } from './tabs/CaseUrgencyTab';
import { CasesTab } from './tabs/CasesTab';
import { TraitsTab } from './tabs/TraitsTab';
import { ContentPreviewsTab } from './tabs/ContentPreviewsTab';
import { AccessTab } from './tabs/AccessTab';
import { OverviewTab } from './tabs/OverviewTab';
import { UxPreferencesTab } from './tabs/UxPreferencesTab';
import { useQuery } from '../App';
import { PolicyDefinitionsTab } from './tabs/PolicyDefinitionsTab';

export interface StateITF {
  passedQueueData: QueueConfiguration;
  caseId?: string;
}

interface SettingsRouteParams {
  tenantId: string;
  titleId: string;
  subjectType: string;
}

export const enum TabKey {
  OverView = 'overview',
  CaseUrgency = 'caseurgency',
  Cases = 'cases',
  Traits = 'traits',
  PolicyDefinition = 'policydefinition',
  ContentPreviews = 'contentpreviews',
  Access = 'access',
  UxPreferencesTab = 'uxpreferences'
}

interface SettingsProps extends RouteComponentProps {
  configs: QueueConfiguration[];
}

const tabKey = 'tab';

export const Settings: React.FC<SettingsProps> = props => {
  const { palette, fonts } = getTheme();
  const { state, dispatch } = React.useContext(AdminContext);
  const routeParams: SettingsRouteParams = useParams();
  const routeQueryParams = useQuery();
  const history = useHistory();
  const { search } = useLocation();
  const [currentTab, setCurrentTab] = useState(routeQueryParams.get(tabKey) || TabKey.OverView);

  const isCorrectQueue = (checkQueue: QueueConfiguration): boolean => {
    return (
      checkQueue.tenant.tenantId === routeParams.tenantId &&
      checkQueue.title.titleId === routeParams.titleId &&
      checkQueue.subjectType === routeParams.subjectType
    );
  };

  // Make sure we have a queueDataState to look at the Settings of
  useEffect(() => {
    if (!state.queueDataState || !isCorrectQueue(state.queueDataState)) {
      const relevantQueueConfig = props.configs.find(queueConfig => isCorrectQueue(queueConfig));

      // Unless something weird happens, I expect to always get the config from props at this point
      if (relevantQueueConfig) {
        dispatch({ type: 'LOAD_QUEUE_CONFIG', payload: relevantQueueConfig });
      } else {
        // adding in single config support which will be used once we move the group queue fetch to the dashbord page
        getQueueConfig(routeParams.tenantId, routeParams.titleId, routeParams.subjectType)
          .then(queueConfig => {
            dispatch({ type: 'LOAD_QUEUE_CONFIG', payload: queueConfig });
          })
          .catch(error => {
            trackException(error);
            throw error;
          });
      }
    }
  }, []);

  const pivotStyle: Partial<IPivotStyles> = {
    root: {
      paddingLeft: '4.438rem',
      selectors: {
        '::before': {
          position: 'absolute',
          bottom: 0,
          left: 0,
          background: palette.neutralLight,
          height: 2,
          width: '100%',
          content: `''`
        }
      }
    },
    text: {
      ...fonts.mediumPlus,
      verticalAlign: 'bottom',
      paddingBottom: '	0.625rem'
    },
    link: {
      paddingTop: '0.438rem',
      paddingLeft: 0,
      paddingRight: 0,
      marginRight: '1.125rem',
      height: '100%'
    },
    linkIsSelected: {
      paddingTop: '0.438rem',
      paddingLeft: 0,
      paddingRight: 0,
      marginRight: '1.125rem',
      height: '100%',
      selectors: { '::before': { width: '100%', left: 0, height: 4, borderRadius: 2, paddingRight: 5 } }
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(search);
    // if we haven't loaded a tab yet move on for now
    if (currentTab) {
      if (!params.get(tabKey)) {
        // if there is no tab set, set one and move on
        params.append(tabKey, currentTab);
      } else if (!(params.get(tabKey) === currentTab)) {
        // if there is the wrong tab, delete it and add the right one
        params.delete(tabKey);
        params.append(tabKey, currentTab);
      }
    }

    history.push({ search: params.toString() });
  }, [currentTab, history]);

  useEffect(() => {
    if (state.isUpdated) {
      const percentage: number | undefined =
        state.queueDataState?.caseOpenLifetimeWarningFactor && state.queueDataState.caseOpenLifetimeWarningFactor * 100;

      if (
        checkTheTimeIsValid(state.queueDataState?.caseOpenLifetimeInSeconds, 0, 0) &&
        checkTheTimeIsValid(state.queueDataState?.caseAssignedLifetimeInSeconds, 0, 0) &&
        checkTheTimeIsValid(state.queueDataState?.caseOpenLifetimeMaxInSeconds, 0, 0) &&
        checkThePercentageAndCasesAreValid(percentage, 1) &&
        checkThePercentageAndCasesAreValid(state.queueDataState?.defaultMaxAssignedCaseCount, 1)
      ) {
        dispatch({
          type: 'IS_ACTIVE',
          payload: true
        });
      } else {
        dispatch({
          type: 'IS_ACTIVE',
          payload: false
        });
      }
    }
  }, [state.queueDataState, state.isUpdated]);

  if (!state.queueDataState) {
    return <Spinner size={SpinnerSize.large} className="fixed-top w-100 h-100" />;
  }

  const renderTab = () => {
    if (state.queueDataState) {
      switch (currentTab) {
        case TabKey.OverView:
          return <OverviewTab queueData={state.queueDataState} />;
        case TabKey.CaseUrgency:
          return <CaseUrgencyTab />;
        case TabKey.Cases:
          return <CasesTab />;
        case TabKey.Traits:
          return <TraitsTab queueData={state.queueDataState} changeTab={setCurrentTab} />;
        case TabKey.PolicyDefinition:
          return <PolicyDefinitionsTab queueData={state.queueDataState} />;
        case TabKey.ContentPreviews:
          return <ContentPreviewsTab subjectType={state.queueDataState.subjectType} />;
        case TabKey.UxPreferencesTab:
          return <UxPreferencesTab config={state.queueDataState} />;
        case TabKey.Access:
          return <AccessTab />;
      }
    }
  };

  return (
    <Stack className="h-100" style={{ backgroundColor: palette.neutralLighterAlt }}>
      <Stack.Item>
        <Stack className="h-100 position-relative">
          {state.queueDataState.tenant && state.queueDataState.title && (
            <QueueTitle queueDataState={state.queueDataState} saveActive={state.saveActive} />
          )}
          <nav>
            <Pivot
              linkSize={'large'}
              styles={pivotStyle}
              selectedKey={currentTab}
              onLinkClick={(item?: PivotItem) => setCurrentTab(item?.props.itemKey || TabKey.OverView)}
            >
              {/* Tabs */}
              <PivotItem headerText="Overview" itemKey={TabKey.OverView} />
              <PivotItem headerText="Case Urgency" itemKey={TabKey.CaseUrgency} />
              <PivotItem headerText="Cases and sets" itemKey={TabKey.Cases} />
              <PivotItem headerText="Traits" itemKey={TabKey.Traits} />
              <PivotItem headerText="Trait Definition" itemKey={TabKey.PolicyDefinition} />
              {(state.queueDataState.contentType === ContentType.video ||
                state.queueDataState.contentType === ContentType.image ||
                state.queueDataState.contentType === ContentType.json) && (
                <PivotItem headerText="Content previews" itemKey={TabKey.ContentPreviews} />
              )}
              {state.queueDataState.contentType === ContentType.json && (
                <PivotItem headerText="UX Preferences" itemKey={TabKey.UxPreferencesTab} />
              )}
              <PivotItem headerText="Access and permissions" itemKey={TabKey.Access} />
            </Pivot>
          </nav>
        </Stack>
      </Stack.Item>
      <StackItem grow className="px-5 pt-5 overflow-auto">
        {renderTab()}
      </StackItem>
    </Stack>
  );
};

export default withRouter(Settings);
